import { useCallback, useMemo, useState } from "react"

import { useUser } from "@clerk/nextjs"
import { toast } from "@coval/ui"

import { useRunsFilter } from "../contexts/RunsFilterContext"
import {
  type RunsSavedView,
  type RunsSavedViewConfig,
  type RunsSavedViewsTransportState,
} from "../types/savedViews"

/**
 * State machine for the runs-page saved-views dropdown.
 *
 * Wires transport saved-view mutations, manages the save/edit/delete form
 * state, and exposes grouped views split into "My Views" (personal) and
 * "Organization Views" (shared) based on `config.metadata.isShared`.
 *
 * Does NOT own the filter state — that lives in RunsFilterContext. Applying
 * a view calls `applyView()` on the context, which is responsible for
 * rehydrating every filter field from the config.
 */
export const useRunsSavedViewsState = (
  savedViewsTransport: RunsSavedViewsTransportState,
) => {
  const { user } = useUser()

  const {
    searchValue,
    dateRange,
    statusFilters,
    templateFilters,
    personaFilters,
    agentFilters,
    metricFilters,
    metricValueFilters,
    tagFilters,
    mutationFilters,
    testSetFilters,
    testCaseFilters,
    metadataFilters,
    scheduledJobFilters,
    sortDescriptor,
    loadedViewId,
    applyView,
    clearLoadedView,
  } = useRunsFilter()

  const [editingViewId, setEditingViewId] = useState<string | null>(null)
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const [viewName, setViewName] = useState("")
  const [viewDescription, setViewDescription] = useState("")
  const [isShared, setIsShared] = useState(true)

  const {
    clearDefaultView,
    createView,
    deleteView,
    isClearingDefaultView,
    isCreatingView,
    isDeletingView,
    isLoadingViews,
    isSettingDefaultView,
    isUpdatingView,
    savedViews,
    setDefaultView,
    updateView,
    userDefaultView,
  } = savedViewsTransport

  // ── Config builder ─────────────────────────────────────────

  const buildCurrentConfig = useCallback((): RunsSavedViewConfig => {
    return {
      filters: {
        searchValue: searchValue || undefined,
        startDate: dateRange.start?.toISOString() ?? null,
        endDate: dateRange.end?.toISOString() ?? null,
        statusFilters,
        templateFilters,
        personaFilters,
        agentFilters,
        metricFilters,
        metricValueFilters,
        tagFilters,
        mutationFilters,
        testSetFilters,
        testCaseFilters,
        metadataFilters,
        scheduledJobFilters,
        sortDescriptor: {
          column: String(sortDescriptor.column ?? "createdAt"),
          direction: sortDescriptor.direction ?? "descending",
        },
      },
      metadata: {
        isShared,
        description: viewDescription || undefined,
        createdBy: user?.emailAddresses[0]?.emailAddress,
        // scope is stamped by the server — omitted here
      },
    }
  }, [
    searchValue,
    dateRange,
    statusFilters,
    templateFilters,
    personaFilters,
    agentFilters,
    metricFilters,
    metricValueFilters,
    tagFilters,
    mutationFilters,
    testSetFilters,
    testCaseFilters,
    metadataFilters,
    scheduledJobFilters,
    sortDescriptor,
    isShared,
    viewDescription,
    user,
  ])

  // ── Form reset ─────────────────────────────────────────────

  const handleResetForm = useCallback(() => {
    setViewName("")
    setViewDescription("")
    setIsShared(true)
  }, [])

  // ── Handlers ───────────────────────────────────────────────

  const handleSaveCurrentView = useCallback(async () => {
    const name = viewName.trim()
    if (!name) {
      toast.danger({
        title: "Name required",
        description: "Please enter a name for the view.",
      })
      return
    }

    try {
      await createView({
        name,
        config: buildCurrentConfig(),
      })
      toast.success({
        title: "View saved",
        description: `"${name}" has been saved successfully.`,
      })
      handleResetForm()
    } catch (error) {
      toast.error({
        title: "Error saving view",
        error,
        fallback: "Could not save the view.",
      })
    }
  }, [viewName, buildCurrentConfig, createView, handleResetForm])

  const handleRenameView = useCallback(
    async (viewUlid: string, newName: string) => {
      if (!newName.trim()) {
        return
      }
      // Rejects on failure so the view's name field stays open and shows why.
      await updateView({
        ulid: viewUlid,
        name: newName.trim(),
      })
      toast.success({
        title: "View updated",
        description: "The view has been updated successfully.",
      })
      setEditingViewId(null)
    },
    [updateView],
  )

  const handleDeleteView = useCallback(
    async (viewUlid: string) => {
      try {
        await deleteView({
          ulid: viewUlid,
        })
        toast.success({
          title: "View deleted",
          description: "The view has been deleted successfully.",
        })
        if (loadedViewId === viewUlid) {
          clearLoadedView()
        }
        setConfirmDeleteId(null)
      } catch (error) {
        toast.error({
          title: "Error deleting view",
          error,
          fallback: "Could not delete the view.",
        })
      }
    },
    [clearLoadedView, deleteView, loadedViewId],
  )

  const handleLoadView = useCallback(
    (view: RunsSavedView) => {
      applyView(view)
    },
    [applyView],
  )

  const handleSetDefault = useCallback(
    async (viewUlid: string) => {
      try {
        await setDefaultView({
          ulid: viewUlid,
        })
        toast.success({
          title: "Default view set",
          description: "This view will load automatically next time.",
        })
      } catch (error) {
        toast.error({
          title: "Error setting default",
          error,
          fallback: "Could not set the default view.",
        })
      }
    },
    [setDefaultView],
  )

  const handleClearDefault = useCallback(async () => {
    try {
      await clearDefaultView()
    } catch (error) {
      toast.error({
        title: "Error clearing default",
        error,
        fallback: "Could not clear the default view.",
      })
    }
  }, [clearDefaultView])

  // ── Grouped views ──────────────────────────────────────────

  const groupedViews = useMemo(() => {
    if (!savedViews || !user) {
      return {
        orgViews: [] as RunsSavedView[],
        myViews: [] as RunsSavedView[],
      }
    }

    const orgViews = savedViews.filter(
      (view) => view.config.metadata?.isShared === true,
    )
    const myViews = savedViews.filter(
      (view) =>
        view.config.metadata?.isShared !== true && view.userId === user.id,
    )

    return { orgViews, myViews }
  }, [savedViews, user])

  // Memoize the `actions` bag so consumers that put it in useCallback deps
  // (e.g., RunsSavedViewsDropdown) don't recreate their callbacks on every
  // render of this hook. The setters from useState are stable; the handlers
  // are wrapped in useCallback already, so identity is stable across renders.
  const actions = useMemo(
    () => ({
      setEditingViewId,
      setConfirmDeleteId,
      setViewName,
      setViewDescription,
      setIsShared,
      handleResetForm,
      handleSaveCurrentView,
      handleRenameView,
      handleDeleteView,
      handleLoadView,
      handleSetDefault,
      handleClearDefault,
      clearLoadedView,
    }),
    [
      handleResetForm,
      handleSaveCurrentView,
      handleRenameView,
      handleDeleteView,
      handleLoadView,
      handleSetDefault,
      handleClearDefault,
      clearLoadedView,
    ],
  )

  return {
    state: {
      editingViewId,
      confirmDeleteId,
      viewName,
      viewDescription,
      isShared,
      loadedViewId,
      userDefaultViewUlid: userDefaultView?.ulid ?? null,
    },
    actions,
    data: {
      savedViews,
      isLoadingViews,
      groupedViews,
    },
    mutations: {
      createView: { isPending: isCreatingView },
      updateView: { isPending: isUpdatingView },
      deleteView: { isPending: isDeletingView },
      setDefault: { isPending: isSettingDefaultView },
      clearDefault: { isPending: isClearingDefaultView },
    },
  }
}
